import { motion } from "framer-motion";
import { Box, Typography, Grid, Card } from "@mui/material";
import React from "react";
import SecurityIcon from "@mui/icons-material/Security";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import HotelIcon from "@mui/icons-material/Hotel";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import AnchorIcon from "@mui/icons-material/Anchor";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import WorkIcon from "@mui/icons-material/Work";

const FeatureButton = ({ icon, title, description, theme, t }) => {
  return (
    <motion.div
      whileHover="hover"
      initial="rest"
      animate="rest"
      variants={{
        rest: { scale: 1 },
        hover: { scale: 1.05 },
      }}
      style={{ width: "100%", height: "100%" }}
    >
      <Card
        sx={{
          p: 4,
          borderRadius: "20px",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          background: theme.palette.background.paper,
          boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Icon and Title always visible */}
        <Box sx={{ color: theme.palette.primary.main, mb: 2 }}>
          {React.cloneElement(icon, { sx: { fontSize: 60 } })}
        </Box>
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, transition: "color 0.3s" }}
        >
          {t(title)}
        </Typography>

        {/* Description revealed on hover */}
        <motion.div
          variants={{
            rest: { opacity: 0, y: 20 },
            hover: { opacity: 1, y: 0 },
          }}
          transition={{ duration: 0.3 }}
          style={{
            position: "absolute",
            bottom: 30,
            left: 20,
            right: 20,
          }}
        >
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ backgroundColor: "background.paper", p: 1, borderRadius: 1 }}
          >
            {t(description)}
          </Typography>
        </motion.div>
      </Card>
    </motion.div>
  );
};

export default function WhoIsForSection({ theme, t }) {
  const features = [
    {
      icon: <MilitaryTechIcon />,
      title: "Army Shifts",
      description:
        "Coordinate duty schedules and rotations for military units.",
    },
    {
      icon: <LocalHospitalIcon />,
      title: "Hospitals",
      description:
        "Organize doctors, nurses, and staff schedules with precision.",
    },
    {
      icon: <SecurityIcon />,
      title: "Security Groups",
      description:
        "Easily schedule guards, assign shifts, and manage rotations.",
    },
    {
      icon: <HotelIcon />,
      title: "Hotels",
      description:
        "Manage rotating personnel shifts and ensure smooth operations.",
    },
    {
      icon: <AnchorIcon />,
      title: "Coast Guard",
      description:
        "Streamline shifts and emergency readiness for coast guard teams.",
    },
    {
      icon: <LocalFireDepartmentIcon />,
      title: "Firefighters",
      description:
        "Manage on-call schedules, rotations, and emergency coverage.",
    },
    {
      icon: <WorkIcon />,
      title: "More",
      description:
        "Adaptable to any organization needing fair and efficient scheduling.",
    },
  ];

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        px: { xs: 2, md: 4 },
        textAlign: "center",
        maxWidth: "1300px",
        mx: "auto",
      }}
    >
      <Typography
        variant="h3"
        component="h2"
        sx={{
          fontWeight: 800,
          mb: 8,
          color: "#1976d2",
          letterSpacing: "-0.5px",
        }}
      >
        {t("Made For You:")}
      </Typography>

      <Grid container spacing={5} justifyContent="center">
        {features.map((feature, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <FeatureButton {...feature} theme={theme} t={t} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
